<template>
  <el-dialog
    v-model="visible"
    :draggable="true"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :fullscreen="isFullscreen"
    v-bind="$attrs"
    id="my-dialog"
    @open="handleOpen"
    close-icon="CloseBold"
    destroy-on-close
    append-to-body
    top="5vh"
  >
    <template #header>
      <div class="header">
        <div class="title">
          {{ props.title }}
        </div>
        <div class="utils">
          <el-icon
            v-if="!$attrs.hasOwnProperty('fullscreen')"
            class="full"
            @click="toggleFullscreen"
          >
            <FullScreen />
          </el-icon>
        </div>
      </div>
    </template>
    <div id="body" :style="{ backgroundColor: bodyBgColor }">
      <slot></slot>
    </div>
  </el-dialog>
</template>

<script setup>
import { onBeforeUnmount, ref, watch, nextTick } from "vue";

// 👇 定义事件：传给父组件
const emits = defineEmits(["open", "fullscreen-change"]);
const props = defineProps(["title", "bodyBgColor"]);
const visible = defineModel();
const isFullscreen = ref(false);
let dialogEl = null;

watch(isFullscreen, (newVal) => {
  if (newVal) {
    resetDialogStyle();
  }

  nextTick(() => {
    const bodyEl = document.getElementById('body');
    if (!bodyEl) return;

    bodyEl.style.height = '0';
    setTimeout(() => {
      bodyEl.style.height = '100%';
      
      // ==============================================
      // 🔥 🔥 🔥 关键：全屏切换后把高度传给父组件
      // ==============================================
      emitDialogHeight();
    }, 0);
  });
});

// 👇 封装：获取弹窗高度并抛给父组件
const emitDialogHeight = () => {
  nextTick(() => {
    const dialog = document.getElementById('my-dialog');
    if (dialog) {
      const height = dialog.offsetHeight;
      // 👇 向父组件抛出：是否全屏 + 当前弹窗高度
      emits("fullscreen-change", {
        isFullscreen: isFullscreen.value,
        dialogHeight: height
      });
    }
  });
};

// 弹框打开回调
const handleOpen = () => {
  if (!dialogEl) dialogEl = document.getElementById("my-dialog");
  installDragZoom();
  emits("open");
};

// 切换全屏
const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

// 重置弹框宽高
const resetDialogStyle = () => {
  if (dialogEl) {
    dialogEl.style.width = "";
    dialogEl.style.height = "";
  }
};

// 安装拖动缩放
const installDragZoom = () => {
  if (!dialogEl) return;

  const minWidth = 200;
  const minHeight = 200;
  const resizeEl = document.createElement("div");
  dialogEl.appendChild(resizeEl);

  resizeEl.style.cursor = "se-resize";
  resizeEl.style.position = "absolute";
  resizeEl.style.height = "10px";
  resizeEl.style.width = "10px";
  resizeEl.style.right = "0px";
  resizeEl.style.bottom = "0px";
  resizeEl.style.zIndex = "999";

  resizeEl.onmousedown = (e) => {
    const clientX = e.clientX;
    const disX = e.clientX - resizeEl.offsetLeft;
    const disY = e.clientY - resizeEl.offsetTop;

    document.onmousemove = function (e) {
      if (isFullscreen.value) return;
      e.preventDefault();

      const x = e.clientX - disX + (e.clientX - clientX);
      const y = e.clientY - disY;
      dialogEl.style.width = x > minWidth ? `${x}px` : `${minWidth}px`;
      dialogEl.style.height = y > minHeight ? `${y}px` : `${minHeight}px`;
    };

    document.onmouseup = function () {
      document.onmousemove = null;
      document.onmouseup = null;
    };
  };
};

onBeforeUnmount(() => {
  document.onmousemove = null;
  document.onmouseup = null;
});
</script>

<style lang="scss">
#my-dialog {
  padding: 0 !important;
  border-radius: 10px;
  overflow: hidden;
  z-index: 999;
  .el-dialog__header {
    padding: 0;

    .header {
      background-color: var(--themed-color2);
      padding: 0 10px 0 20px;
      color: #fff;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 50px;

      .title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 18px;

        &::before {
          content: "";
          display: inline-block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          padding: 5px;
          border: 5px solid #fff;
        }
      }

      .utils {
        display: flex;
        align-items: center;
        height: 100%;
        transform: translateX(-30px);
        * {
          font-size: 25px;
        }
        i {
          cursor: pointer;
          font-size: 25px;
          height: 100%;
          width: 40px;
          text-align: center;
        }

        .close,
        .full {
          &:hover {
            transition: 0.3s;
            transform: rotate(90deg);
          }
        }
      }
    }

    .el-dialog__headerbtn {
      * {
        font-size: 28px;
      }
      .el-dialog__close {
        margin-top: 6px;
        color: #fff;

        &:hover {
          transition: 0.3s;
          transform: rotate(90deg);
        }
      }
    }
  }

  .el-dialog__body {
    width: 100%;
    height: calc(100% - 50px);
  }

  #body {
    padding: 20px;
    height: 100%;
    width: 100%;
    overflow: hidden;
    overflow: auto;
    box-sizing: border-box;
  }
}
</style>